/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import dayjs from "dayjs";
import { useApplicationEnvironmentQuery } from "~/generated/graphql";
import { useCurrency } from "~/hooks";
import { isNotNil } from "~/tools";
import { mapEnvironment } from "~/tools/applicationMapper";

type Props = {
  applicationId: string;
  page: number;
  limit: number;
};

export function useApplicationEnvironments({
  applicationId,
  page,
  limit,
}: Props) {
  const { currency } = useCurrency();

  const { data, loading, error } = useApplicationEnvironmentQuery({
    variables: {
      id: applicationId,
      year: dayjs().year().toString(10),
      page,
      limit,
    },
  });

  const environments = data?.application?.environmentsV2;

  const mappedEnvironments = useMemo(
    () =>
      environments?.environments
        ?.filter(isNotNil)
        .map((environment) =>
          mapEnvironment(environment, currency, applicationId)
        ) ?? [],
    [applicationId, currency, environments?.environments]
  );

  return {
    count: environments?.count ?? 0,
    environments: mappedEnvironments,
    loading,
    error,
  };
}
